/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * Visit cards on My profile › Memberships: visits left, the real dates, and
 * where to buy more.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Badge, Button, Progress, Skeleton, cn } from "@lemonadetraining/web-ui";
import {
  getActiveLandingPageForBox,
  getUserVisitCards,
  useAppSelector,
} from "@lemonadetraining/web-shared";
import { formatCommitmentDate, toDate } from "@lemonadetraining/web-shared/utils/commitmentStatus";
import type { VisitCardDisplay as VisitCardDisplayType } from "@lemonadetraining/contracts";
import { IconExternalLink } from "@tabler/icons-react";
import { useOwnGymZone } from "../../hooks/useOwnGymZone";
import { PendingDate } from "../time/PendingDate";

/* The callable returns Timestamps as {_seconds, _nanoseconds}; toDate reads both shapes. */
const formatDay = (value: unknown, zone: string | null | undefined): React.ReactNode => {
  const date = toDate(value);
  if (!date) return null;
  return zone === undefined ? <PendingDate /> : formatCommitmentDate(date, zone);
};

const statusChip = (card: VisitCardDisplayType) => {
  if (card.status === "expired") return { label: "Expired", variant: "secondary" as const };
  if (card.status === "used_up" || card.visitsRemaining <= 0) {
    return { label: "Used up", variant: "secondary" as const };
  }
  return { label: `${card.visitsRemaining} left`, variant: "default" as const };
};

const fillClass = (card: VisitCardDisplayType): string => {
  if (card.status !== "active") return "bg-muted-foreground";
  if (card.usagePercentage >= 80) return "bg-destructive";
  if (card.usagePercentage >= 50) return "bg-warning";
  return "bg-success";
};

const useGymPageLinks = (boxIDs: string[]) => {
  const [links, setLinks] = useState<Record<string, string | null>>({});
  const key = boxIDs.join("|");
  useEffect(() => {
    let live = true;
    const websiteUrl = process.env.REACT_APP_WEBSITE_URL || "http://localhost:3000";
    key
      .split("|")
      .filter(Boolean)
      .forEach((boxID) => {
        getActiveLandingPageForBox(boxID)
          .then((page: { name?: string } | null) => {
            if (!live) return;
            setLinks((prev) => ({
              ...prev,
              [boxID]: page?.name ? `${websiteUrl}/landing-page/${page.name}` : null,
            }));
          })
          .catch(() => live && setLinks((prev) => ({ ...prev, [boxID]: null })));
      });
    return () => {
      live = false;
    };
  }, [key]);
  return links;
};

const VisitCardDisplay: React.FC = () => {
  const uid = useAppSelector((state) => state.user.user?.uid);
  const [visitCards, setVisitCards] = useState<VisitCardDisplayType[] | null>(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    if (!uid) return;
    setFailed(false);
    setVisitCards(null);
    try {
      const result = (await getUserVisitCards()) as { data?: { visitCards?: unknown } };
      const cards = result?.data?.visitCards;
      setVisitCards(Array.isArray(cards) ? (cards as VisitCardDisplayType[]) : []);
    } catch (err) {
      console.error("Error loading visit cards:", err);
      setFailed(true);
    }
  }, [uid]);

  useEffect(() => {
    load();
  }, [load]);

  const { zone } = useOwnGymZone((visitCards?.length ?? 0) > 0);
  const links = useGymPageLinks([...new Set((visitCards || []).map((card) => card.boxID))]);

  if (failed) {
    return (
      <div role="alert" className="flex flex-wrap items-center gap-3 text-sm">
        <span className="text-destructive">We couldn&apos;t load your visit cards.</span>
        <Button variant="outline" className="min-h-[44px]" onClick={load}>
          Try again
        </Button>
      </div>
    );
  }

  if (visitCards === null) {
    return (
      <div aria-busy="true" data-testid="visit-cards-loading">
        <span className="sr-only">Loading visit cards</span>
        <Skeleton className="h-32 w-full rounded-lg" />
      </div>
    );
  }

  if (visitCards.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="visit-cards-empty">
        You don&apos;t have any visit cards.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {visitCards.map((card) => {
        const chip = statusChip(card);
        const expires = formatDay(card.expiresAt, zone);
        const lastUsed = formatDay(card.lastUsedAt, zone);
        const bought = formatDay(card.createdAt, zone);
        const soon =
          card.status === "active" &&
          typeof card.daysUntilExpiration === "number" &&
          card.daysUntilExpiration <= 7;
        const buyHref = links[card.boxID];
        return (
          <div
            key={card.productUID}
            className="flex flex-col gap-3 rounded-lg border px-4 py-3"
            data-testid={`visit-card-${card.productUID}`}
          >
            <div className="flex items-start gap-3">
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="text-sm font-medium">{card.membershipName}</span>
                <span className="text-xs text-muted-foreground">{card.boxName}</span>
              </div>
              <Badge variant={chip.variant} className="shrink-0" data-testid="visit-card-status">
                {chip.label}
              </Badge>
            </div>

            <Progress
              value={card.usagePercentage}
              className="h-2"
              indicatorClassName={fillClass(card)}
              aria-label={`${card.visitsUsed} of ${card.visitsTotal} visits used`}
            />

            <dl className="grid gap-3 text-sm sm:grid-cols-3">
              <div className="flex flex-col">
                <dt className="text-xs text-muted-foreground">Visits left</dt>
                <dd className="font-medium">
                  {card.visitsRemaining} of {card.visitsTotal}
                </dd>
              </div>
              {expires && (
                <div className="flex flex-col">
                  <dt className="text-xs text-muted-foreground">Use them by</dt>
                  <dd
                    className={cn("font-medium", soon && "text-destructive")}
                    data-testid="visit-card-expires"
                  >
                    {expires}
                  </dd>
                  <dd className="text-xs text-muted-foreground">
                    {soon &&
                    card.daysUntilExpiration !== null &&
                    card.daysUntilExpiration !== undefined
                      ? `${card.daysUntilExpiration === 1 ? "1 day" : `${card.daysUntilExpiration} days`} to go. Unused visits end then.`
                      : "Unused visits end then."}
                  </dd>
                </div>
              )}
              <div className="flex flex-col">
                <dt className="text-xs text-muted-foreground">Last used</dt>
                <dd className="font-medium" data-testid="visit-card-last-used">
                  {lastUsed || "Not used yet"}
                </dd>
                {bought && <dd className="text-xs text-muted-foreground">Bought {bought}</dd>}
              </div>
            </dl>

            {buyHref && (
              <div className="flex flex-wrap items-center gap-2">
                <Button asChild variant="outline" className="min-h-[44px]">
                  <a href={buyHref} target="_blank" rel="noreferrer" data-testid="visit-card-buy">
                    Buy more visits
                    <IconExternalLink aria-hidden="true" className="size-4" />
                    <span className="sr-only"> (opens your gym&apos;s page in a new tab)</span>
                  </a>
                </Button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default VisitCardDisplay;
